import type { Store, TypedStartListening } from '@reduxjs/toolkit'
import { createListenerMiddleware } from '@reduxjs/toolkit'

import { connectAnalyticsConsent } from '@capture/client/analytics/analyticsConsent'
import { analyticsMiddleware } from '@capture/client/analytics/analyticsMiddleware'
import { connectIntervalPoller } from '@capture/client/API/services/IntervalPoller'
import { connectSyncers } from '@capture/client/API/syncers'
import { makeSentryMiddleware } from '@capture/client/errorReporting/sentryMiddleware'
import { makeStore } from '@capture/client/state'
import { albumMiddleware } from '@capture/client/state/album/autoAlbumMiddleware'
import { carouselViewMiddleware } from '@capture/client/state/carouselViewer/carouselMiddleware'
import { connectUserGuarantee } from '@capture/client/state/currentUser'
import { changesSyncerMiddleware } from '@capture/client/state/job/changesSyncerMiddleware'
import { addModalListeners } from '@capture/client/state/modalManager/modalManagerSlice'
import { popupMiddleware } from '@capture/client/state/popup/popupMiddleware'
import { addResumeListeners } from '@capture/client/state/resume/resumeSlice'
import type { mainReducer } from '@capture/client/state/store'
import { trashMiddleware } from '@capture/client/state/trash/trashMiddleware'
import { connectUploader } from '@capture/client/state/uploader/uploadQueue'

import { SENTRY_DSN__FULL_WEB } from './config/constants'

const listenerMiddleware = createListenerMiddleware()
export const startAppListening = listenerMiddleware.startListening as TypedStartListening<
    StateOfReducer<ReturnType<typeof mainReducer>>
>

// register the resume listeners
addResumeListeners(startAppListening)
addModalListeners(startAppListening)

let store: Store | null = null

export function initializeStore() {
    return makeStore(
        [
            makeSentryMiddleware({ dsn: SENTRY_DSN__FULL_WEB }),
            analyticsMiddleware,
            changesSyncerMiddleware,
            popupMiddleware,
            carouselViewMiddleware,
            albumMiddleware,
            trashMiddleware,
        ],
        [
            connectUploader,
            connectSyncers,
            connectUserGuarantee,
            connectIntervalPoller,
            connectAnalyticsConsent,
        ],
        listenerMiddleware,
    )
}

export function getStore() {
    if (!store) {
        store = initializeStore()
    }

    return store
}

/**
 * Reset the store to a fresh instance.
 * Used by Storybook to ensure each story starts with clean state.
 */
export function resetStore() {
    store = initializeStore()
    return store
}
